import { apiClient } from '@/api/api'
import type { MenuRoot } from '@/types/menuItemTypes'

/**
 * Backend nodes are arbitrary-depth and use `children: null` for leaves, while
 * the menu type expects exactly three levels with arrays throughout. Depth
 * beyond the third level is dropped rather than flattened — the nav has no
 * place to render it.
 */
interface CategoryTreeNode {
  id: string | number
  label: string
  children?: CategoryTreeNode[] | null
}

interface CategoryTreeResponse {
  category_tree?: CategoryTreeNode[] | null
}

function toMenuRoot(nodes: CategoryTreeNode[]): MenuRoot {
  return nodes.map((root) => ({
    id: String(root.id),
    label: root.label,
    children: (root.children ?? []).map((child) => ({
      id: String(child.id),
      label: child.label,
      children: (child.children ?? []).map((leaf) => ({
        id: String(leaf.id),
        label: leaf.label
      }))
    }))
  }))
}

export const getCategoryTree = async (): Promise<MenuRoot> => {
  const response = await apiClient.get<CategoryTreeResponse>('/shopping-list/category-tree')
  return toMenuRoot(response.data?.category_tree ?? [])
}

/* ---------------------------------------------------------------------- */
/* v1 — the burger menu's tree (CATEGORY-TREE, 2026-09-14)                */
/* ---------------------------------------------------------------------- */

import { apiV1Client } from '@/api/api'

/** The header tabs the backend serves a tree for — its own spelling. */
export type CategoryTreeAudience = 'woman' | 'man' | 'kids'
export type CategoryTreeLang = 'en' | 'ru'

/** What the results page is opened with — passed through, never rebuilt. */
export interface CategoryNodeSearch {
  audience: CategoryTreeAudience
  /** The node's slug; the page sends `filters.categories: ["n.<slug>"]`. */
  category_node: string
}

export interface CategoryTreeNodeV1 {
  node_id: number | null
  slug: string
  name: string
  /** The `total` a click shows — counted the same way by the backend. */
  count: number
  search: CategoryNodeSearch
  children: CategoryTreeNodeV1[]
}

export interface CategoryTreeGroupV1 {
  key: string
  node_id: number | null
  name: string
  count: number
  search: CategoryNodeSearch
  nodes: CategoryTreeNodeV1[]
}

export interface CategoryTreeHiddenV1 {
  slug: string
  count: number
  live: number
}

export interface CategoryTreeResponseV1 {
  audience: CategoryTreeAudience
  lang: CategoryTreeLang
  /** The index refill the counts were taken under. */
  generation: string
  min_count: number
  groups: CategoryTreeGroupV1[]
  flat: CategoryTreeNodeV1[]
  hidden: CategoryTreeHiddenV1[]
}

/**
 * `GET /api/v1/categories/tree` — nodes of the normalised taxonomy, counted
 * per tab on the serving index. 404 = no tree to serve (the caller shows
 * its error state), 422 = a value outside the two closed sets.
 */
export const getCategoryTreeV1 = async (
  audience: CategoryTreeAudience,
  lang: CategoryTreeLang,
): Promise<CategoryTreeResponseV1> => {
  const { data } = await apiV1Client.get<CategoryTreeResponseV1>('categories/tree', {
    params: { audience, lang },
  })
  return data
}
